import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

// routes
import AuthenticationRoutes from './AuthenticationRoutes';
import LoginRoutes from './LoginRoutes';
import MainRoutes from './MainRoutes';
import SimpleRoutes from './SimpleRoutes';
import ChatRoutes from './ChatRoutes';
import BrainRoutes from './BrainRoutes';
// project imports
import Loadable from 'ui-component/Loadable';

const SplashScreen = Loadable(lazy(() => import('views/splash/SplashScreen')));

// ==============================|| SPLASH GATE ||============================== //
// Policy: "reproduce, but don't nag."
//   • Reproduce — show the splash on the first app load of a fresh work session
//     (arriving at work, reopening the browser, or returning after a long break).
//   • Don't nag — frequent reloads, opening links in new tabs, and in-app
//     navigation during active work must NOT interrupt the user.
//
// Implementation: a localStorage timestamp of the last app load. If the current
// load is more than FRESH_SESSION_GAP_MS after the previous one, it's treated as
// a fresh session and we rewrite the URL to /splash (preserving the original
// destination in `next`). The timestamp is refreshed on EVERY load, so the gap
// measures idle time — a long, busy day of reloads never re-triggers the splash.
//
// Must run BEFORE createBrowserRouter is called below, since the router reads
// `location.pathname` once at creation time. Keep SPLASH_TS_KEY in sync with the
// SplashScreen component.
export const SPLASH_TS_KEY = 'phnx_splash_last_seen';
const FRESH_SESSION_GAP_MS = 4 * 60 * 60 * 1000; // 4 hours

(function ensureSplashFirst() {
  if (typeof window === 'undefined') return;
  try {
    const now = Date.now();
    const last = Number(window.localStorage.getItem(SPLASH_TS_KEY)) || 0;
    const isFreshSession = now - last > FRESH_SESSION_GAP_MS;

    // Always record this load as the latest activity.
    window.localStorage.setItem(SPLASH_TS_KEY, String(now));

    if (!isFreshSession) return;

    const base = (import.meta.env.VITE_APP_BASE_NAME || '').replace(/\/$/, '');
    const path = window.location.pathname;
    const rel = base && path.startsWith(base) ? path.slice(base.length) || '/' : path;
    if (rel === '/splash') return;
    const next = encodeURIComponent(rel + window.location.search + window.location.hash);
    window.history.replaceState({}, '', `${base}/splash?next=${next}`);
  } catch {
    /* storage unavailable — skip the splash gate (prefer not nagging) */
  }
})();

// ==============================|| ROUTING RENDER ||============================== //

const router = createBrowserRouter(
  [
    // Splash-first is enforced by ensureSplashFirst() above (history rewrite on a
    // fresh tab). Once the splash has been seen, "/" should fall through to the
    // app instead of bouncing back to the splash, otherwise pressing ENTER with
    // next="/" would show the splash a second time.
    { path: '/', element: <Navigate to="/login" replace /> },
    { path: '/splash', element: <SplashScreen /> },
    AuthenticationRoutes,
    LoginRoutes,
    SimpleRoutes,
    BrainRoutes,
    MainRoutes,
    ChatRoutes
  ],
  {
    basename: import.meta.env.VITE_APP_BASE_NAME
  }
);

export default router;
